import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Eye, Skull, VenetianMask, Crown, Key, Ghost, Compass, Anchor, Brain, Flame, Zap, Moon, Telescope, Scale, ScrollText, Feather, Radio, Sparkles, Atom, Snowflake, ShieldAlert, ShieldCheck, Swords, Target, Fingerprint, Spade, Gavel, HelpCircle, Image as ImageIcon, Ship, Users } from "lucide-react";
import CardArtwork from "./CardArtwork";
import VaultCardDetailModal from "./VaultCardDetailModal";

// Card icon registry — mirror the catalog `icon` field. Still used for the
// hidden silhouette state.
const ICONS = {
  eye: Eye, skull: Skull, "venetian-mask": VenetianMask, crown: Crown, lock: Lock, key: Key,
  ghost: Ghost, compass: Compass, anchor: Anchor, brain: Brain, flame: Flame, zap: Zap,
  moon: Moon, telescope: Telescope, scale: Scale, scroll: ScrollText, feather: Feather,
  radio: Radio, sparkles: Sparkles, atom: Atom, snowflake: Snowflake,
  "shield-alert": ShieldAlert, "shield-check": ShieldCheck, swords: Swords, target: Target,
  fingerprint: Fingerprint, spade: Spade, gavel: Gavel, "question-mark": HelpCircle,
  image: ImageIcon, ship: Ship, "users-2": Users,
};

const RARITY_LABEL = {
  common: "COMMON", uncommon: "UNCOMMON", rare: "RARE",
  mythic: "MYTHIC", classified: "CLASSIFIED", divine: "DIVINE",
  celestial: "CELESTIAL",
};

const SUBJECT_LABEL = {
  person: "PERSON", event: "EVENT", document: "DOCUMENT",
  artifact: "ARTIFACT", place: "PLACE", concept: "CONCEPT",
  operation: "OPERATION",
};

const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#00ff88",
  rare: "#22d3ee",
  mythic: "#a78bfa",
  classified: "#ff3b30",
  divine: "#ffd700",
  celestial: "#d946ef",
};

/**
 * VaultCard — hybrid full-art brutalist card.
 *
 *   Layout (vertical):
 *     [TOP STRIPE — rarity color]
 *     CATEGORY ROW (subject type · rarity label · copy #)
 *     ARTWORK — big icon on color-tinted gradient
 *     NAME (heading)
 *     FLAVOR (2-line excerpt)
 *     STATS STRIP — bottom corner (cred / impact / disturb)
 *     [BOTTOM STRIPE]
 *
 * Variants:
 *   - `revealed`: full card with art, name, flavor, stats
 *   - `silhouette`: hidden card (locked icon, "???" name, rarity color preserved)
 *
 * Classified cards get a shimmering holographic overlay (CSS in App.css).
 *
 * Props:
 *   card           — object from catalog
 *   editionNumber  — copy number (the int)
 *   copies         — total copies owned (badge)
 *   variant        — "revealed" (default) | "silhouette"
 *   size           — "md" (default) | "sm" | "lg"
 *   onClick        — optional explicit click handler. If omitted and `clickable`
 *                    is true, clicking opens the built-in CardDetailModal.
 *   clickable      — when true (default), clicking the card pops the detail modal
 *   linkToDossier  — when true AND has subject_dossier_slug, wraps in a Link
 *                    (suppresses the detail modal — the link wins)
 */
export default function VaultCard({
  card,
  editionNumber,
  copies,
  variant = "revealed",
  size = "md",
  onClick,
  clickable = true,
  linkToDossier = false,
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const rarity = card?.rarity || "common";
  const Icon = ICONS[card?.icon] || HelpCircle;
  const subjectLabel = SUBJECT_LABEL[card?.subject_type] || (card?.subject_type || "").toUpperCase();
  const rarityLabel = RARITY_LABEL[rarity] || rarity.toUpperCase();
  const isHidden = variant === "silhouette";
  const isClassified = rarity === "classified" && !isHidden;
  const isDivine = rarity === "divine" && !isHidden;
  const isCelestial = rarity === "celestial" && !isHidden;
  const isMythic = rarity === "mythic" && !isHidden;
  const isRare = rarity === "rare" && !isHidden;
  const rarityColor = RARITY_COLOR[rarity] || "#94a3b8";

  const sizes = {
    sm: { card: "w-[160px] h-[224px]", iconBoxPx: 96,  iconSize: 44, nameClass: "text-sm",  flavorClass: "text-[10px] leading-snug line-clamp-2" },
    md: { card: "w-[200px] h-[280px]", iconBoxPx: 128, iconSize: 56, nameClass: "text-base", flavorClass: "text-[10px] leading-snug line-clamp-3" },
    lg: { card: "w-[260px] h-[364px]", iconBoxPx: 176, iconSize: 76, nameClass: "text-lg",   flavorClass: "text-[11px] leading-snug line-clamp-4" },
  }[size];

  // Decide what happens on click. Explicit onClick wins; otherwise default to
  // opening the detail modal (unless linkToDossier mode is on, where the Link
  // wrapper handles navigation).
  const handleClick = onClick
    ? onClick
    : (clickable && !linkToDossier && !isHidden ? () => setDetailOpen(true) : undefined);

  const inner = (
    <div
      data-testid={`vault-card-${card?.id || "unknown"}`}
      data-rarity={rarity}
      data-variant={variant}
      className={`group/card relative ${sizes.card} bg-[#0a0a0a] border flex flex-col overflow-hidden transition-all duration-200 ${
        handleClick || linkToDossier ? "cursor-pointer hover:translate-y-[-2px]" : ""
      } ${isClassified ? "vc-classified-shimmer vc-classified-glow" : ""} ${isDivine ? "vc-divine-shimmer vc-divine-glow" : ""} ${isCelestial ? "vc-celestial-prism vc-celestial-glow" : ""} ${isMythic ? "vc-mythic-glow" : ""} ${isRare ? "vc-rare-shine vc-rare-glow" : ""}`}
      style={{ borderColor: isClassified || isDivine || isCelestial || isMythic || isRare ? rarityColor : `${rarityColor}55` }}
      onClick={handleClick}
    >
      {/* TOP RARITY STRIPE */}
      <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: rarityColor }} />

      {/* CATEGORY ROW */}
      <div className="px-2.5 py-1.5 flex items-center justify-between font-mono text-[9px] tracking-[0.15em] relative z-[3]" style={{ borderBottom: `1px solid ${rarityColor}22` }}>
        <span className="text-zinc-500 truncate">{isHidden ? "???" : subjectLabel}</span>
        <span className="font-bold shrink-0 ml-1" style={{ color: rarityColor }}>{rarityLabel}</span>
      </div>

      {/* ARTWORK */}
      <div
        className="relative flex-shrink-0 overflow-hidden"
        style={{ height: sizes.iconBoxPx }}
      >
        {isHidden ? (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: `radial-gradient(circle at 50% 45%, ${rarityColor}33 0%, transparent 65%), repeating-linear-gradient(45deg, ${rarityColor}06 0 6px, transparent 6px 12px)`,
            }}
          >
            {/* Grain overlay */}
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='80' height='80' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
            }} />
            <Icon size={sizes.iconSize} color={rarityColor} strokeWidth={1.2} opacity={0.18} />
            <Lock size={Math.round(sizes.iconSize * 0.45)} className="absolute" color={rarityColor} strokeWidth={1.8} />
          </div>
        ) : (
          <CardArtwork card={card} color={rarityColor} height={sizes.iconBoxPx} />
        )}
        {/* Copy-number badge (top-right) */}
        {!isHidden && editionNumber != null && (
          <div
            className="absolute top-1 right-1 font-mono text-[8px] tracking-[0.2em] px-1.5 py-0.5 border bg-[#050505]/80 z-[3]"
            style={{ borderColor: `${rarityColor}66`, color: rarityColor }}
          >
            #{editionNumber}{card.edition_limit ? `/${card.edition_limit}` : ""}
          </div>
        )}
        {/* Duplicate-count badge (bottom-right) */}
        {!isHidden && copies && copies > 1 && (
          <div
            className="absolute bottom-1 right-1 font-mono text-[8px] tracking-[0.2em] px-1.5 py-0.5 bg-white text-[#050505] font-bold z-[3]"
          >
            ×{copies}
          </div>
        )}
      </div>

      {/* NAME */}
      <div className="px-2.5 pt-2 pb-1 relative z-[3]">
        <div className={`font-heading font-black uppercase tracking-tight ${sizes.nameClass} leading-tight ${
          isHidden ? "text-zinc-700" : "text-white"
        }`}>
          {isHidden ? "███ ███████" : card.name}
        </div>
      </div>

      {/* FLAVOR */}
      <div className={`px-2.5 ${sizes.flavorClass} ${isHidden ? "text-zinc-700 italic" : "text-zinc-400"} flex-1 relative z-[3]`}>
        {isHidden
          ? "// Classified — undiscovered."
          : card.flavor}
      </div>

      {/* STATS STRIP */}
      <div
        className="mt-auto px-2.5 py-1.5 flex items-center justify-between gap-1 font-mono text-[9px] tracking-[0.15em] shrink-0 relative z-[3]"
        style={{ borderTop: `1px solid ${rarityColor}22` }}
      >
        <Stat label="CRD" value={card?.stats?.credibility} hidden={isHidden} color={rarityColor} />
        <Stat label="IMP" value={card?.stats?.impact} hidden={isHidden} color={rarityColor} />
        <Stat label="DSB" value={card?.stats?.disturbance} hidden={isHidden} color={rarityColor} />
      </div>

      {/* BOTTOM RARITY STRIPE */}
      <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: rarityColor }} />

      {/* DIVINE — separate aqueous ripple overlay (sibling to the gold
          shimmer pseudo-elements, sits above the artwork like Classified's
          prism overlay). Mounted ONLY for divine cards so it doesn't tax
          the GPU on every card render. */}
      {isDivine && <div className="vc-divine-ripple" aria-hidden />}

      {/* MYTHIC — starfield + nebula cosmos overlay. Sibling div so it can
          drift across the entire card face (above artwork, beneath glow ring).
          Two layers: slow nebula drift + twinkling starfield. */}
      {isMythic && <div className="vc-mythic-cosmos" aria-hidden />}

      {/* CELESTIAL — prismatic cracked-glass overlay. Sits ABOVE Divine in
          the rarity ladder (10-copy hard cap). Two stacked layers inside
          the sibling div: rainbow iridescent sweep + geometric crystal
          shard cracks that bloom from the center. */}
      {isCelestial && <div className="vc-celestial-shards" aria-hidden />}
    </div>
  );

  const wrapped = (linkToDossier && !isHidden && card?.subject_dossier_slug)
    ? <Link to={`/dossier/${card.subject_dossier_slug}`} className="block">{inner}</Link>
    : inner;

  return (
    <>
      {wrapped}
      {detailOpen && (
        <VaultCardDetailModal
          card={card}
          editionNumber={editionNumber}
          copies={copies}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </>
  );
}

function Stat({ label, value, hidden, color }) {
  return (
    <div className="flex flex-col items-center min-w-0">
      <span className="text-zinc-600">{label}</span>
      <span className="font-bold" style={{ color: hidden ? "#404040" : color }}>
        {hidden ? "?" : (value ?? "—")}
      </span>
    </div>
  );
}
